// Web port of mobile/src/services/maintenanceReport.ts — contractor-branded
// Job Completion Report. Builds HTML and pipes it through the same
// WeasyPrint render endpoint the rest of the web PDFs use, then downloads it.
import { htmlToPdf } from "@/pdf/pdfService";
import { LOGO_BASE64 } from "@/pdf/logoAsset";
import { companiesApi } from "@/services/api";

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    let s = v;
    if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) s += "Z";
    const d = new Date(s);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
}

function brandFromContractor(c) {
  if (!c || !c.name) return null;
  return {
    name: String(c.name),
    logo: c.logo_base64 || null,
    address: String(c.address || ""),
    phone: String(c.phone || ""),
    email: String(c.email || ""),
    registration: String(c.registration_number || ""),
    vat: String(c.vat_number || ""),
  };
}

function brandFromCompany(company) {
  return {
    name: String(company?.maintenance_company_name || company?.name || "Maintenance contractor"),
    logo: company?.maintenance_company_logo || null,
    address: [company?.address, company?.postcode].map((v) => String(v || "").trim()).filter(Boolean).join(", "),
    phone: String(company?.phone || ""),
    email: String(company?.email || ""),
    registration: "",
    vat: "",
  };
}

async function resolveBrand(job) {
  const b = brandFromContractor(job?.contractor);
  if (b) return b;
  try {
    const r = await companiesApi.me();
    if (r.data) return brandFromCompany(r.data);
  } catch (e) { console.warn("[pdf] company brand fetch failed (non-fatal)", e); }
  return { name: "Maintenance contractor", logo: null, address: "", phone: "", email: "", registration: "", vat: "" };
}

function headerHtml(brand, title, subtitle) {
  const contact = [brand.address, brand.phone ? `Tel ${brand.phone}` : "", brand.email].filter(Boolean).map(esc).join(" · ");
  const reg = [brand.registration ? `Company No. ${brand.registration}` : "", brand.vat ? `VAT No. ${brand.vat}` : ""].filter(Boolean).map(esc).join(" · ");
  return `
    <div style="border-bottom:3px solid #111827; padding:0 0 12px 0; margin-bottom:16px; display:flex; align-items:flex-start; justify-content:space-between; gap:14px;">
      <div>
        <img src="${esc(brand.logo || LOGO_BASE64)}" alt="${esc(brand.name)} logo" style="max-height:60px; max-width:210px; object-fit:contain;" />
        <div style="font-size:18pt; font-weight:800; color:#111827; margin-top:6px;">${esc(brand.name)}</div>
        ${contact ? `<div style="font-size:9pt; color:#4B5563; margin-top:2px;">${contact}</div>` : ""}
        ${reg ? `<div style="font-size:9pt; color:#4B5563;">${reg}</div>` : ""}
      </div>
      <div style="text-align:right; font-size:10pt; color:#111827;">
        <div style="font-size:16pt; font-weight:800; letter-spacing:0.3px;">${esc(title)}</div>
        <div style="color:#6B7280; margin-top:2px;">${esc(subtitle)}</div>
        <div style="color:#6B7280;">Generated ${fmtDate(new Date())}</div>
      </div>
    </div>`;
}

function footerHtml(brand) {
  return `
    <div style="margin-top:36px; padding-top:12px; border-top:1px solid #E5E7EB; font-size:9pt; color:#6B7280; display:flex; justify-content:space-between;">
      <div>${esc(brand.name)} — Fire Door Maintenance${brand.phone ? ` · Tel ${esc(brand.phone)}` : ""}${brand.email ? ` · ${esc(brand.email)}` : ""}</div>
      <div>Produced using the FD Check app · fdcheck.com</div>
    </div>`;
}

function photoGrid(photos, caption) {
  if (!Array.isArray(photos) || photos.length === 0) return "";
  return `
    <div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">
      ${photos.map((p, i) => `
        <div style="width:150px; text-align:center;">
          <img src="${esc(p)}" style="width:150px; height:110px; object-fit:cover; border-radius:6px; border:1px solid #E5E7EB;" />
          <div style="font-size:8pt; color:#6B7280; margin-top:2px;">${esc(caption)} ${i + 1}</div>
        </div>`).join("")}
    </div>`;
}

function stripCurrency(raw) {
  if (!raw) return "";
  return String(raw)
    .replace(/£\s?\d[\d,]*(?:\.\d+)?\s?[kKmM]?/g, "")
    .replace(/\b(?:GBP|USD|EUR)\s?\d[\d,]*(?:\.\d+)?/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function jobSectionHtml(job) {
  const door = job?.door || {};
  const doorRef = esc(job?.door_reference || door.door_reference || job?.title || "Door");
  const doorWhere = esc([door.location, door.floor ? `Floor ${door.floor}` : "", door.apartment_number ? `Apt ${door.apartment_number}` : ""]
    .map((v) => String(v || "").trim()).filter(Boolean).join(" · "));
  const buildingName = esc(job?.building_name || job?.building?.name || "");
  const status = esc(job?.status || "unknown");
  const priority = esc((job?.priority || "normal").toString());
  const c = job?.completion || {};
  const engineer = esc(c.completed_by_name || job?.engineer_name || "—");
  const scheduledFor = fmtDate(job?.scheduled_for);
  const completedAt = fmtDate(job?.completed_at || c.completed_at);
  const arrivedAt = c.arrival_time ? fmtDate(c.arrival_time) : "";
  const workDone = esc(c.work_done || "");
  const parts = Array.isArray(c.parts_replaced) ? c.parts_replaced : [];
  const generalPhotos = Array.isArray(c.photos) ? c.photos : [];
  const pw = job?.post_work_inspection || null;
  const items = Array.isArray(job?.items) ? job.items : [];
  const itemCompletions = Array.isArray(c.item_completions) ? c.item_completions : [];
  const materials = Array.isArray(c.materials) ? c.materials : [];
  const notes = esc(c.completion_notes || "");
  const signature = esc(c.signature || "");

  return `
    <div style="margin-bottom:20px; border:1px solid #E5E7EB; border-radius:8px; padding:14px 16px; background:#fff;">
      <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px;">
        <div>
          <div style="font-size:14pt; font-weight:800; color:#0F172A;">${doorRef}</div>
          ${doorWhere ? `<div style="font-size:10pt; color:#374151;">${doorWhere}</div>` : ""}
          ${buildingName ? `<div style="font-size:10pt; color:#6B7280;">${buildingName}</div>` : ""}
        </div>
        <div style="text-align:right;">
          <span style="display:inline-block; padding:3px 10px; border-radius:999px; font-size:9pt; font-weight:800;
            background:${status === "completed" ? "#D1FAE5" : "#FEF3C7"};
            color:${status === "completed" ? "#065F46" : "#78350F"};">${status.toUpperCase()}</span>
        </div>
      </div>
      <table style="width:100%; border-collapse:collapse; margin-bottom:10px; font-size:10pt;">
        <tr>
          <td style="width:25%; color:#6B7280; padding:3px 0;">Assigned engineer</td>
          <td style="padding:3px 0; color:#0F172A;">${engineer}</td>
          <td style="width:25%; color:#6B7280; padding:3px 0;">Priority</td>
          <td style="padding:3px 0; color:#0F172A; text-transform:capitalize;">${priority}</td>
        </tr>
        <tr>
          <td style="color:#6B7280; padding:3px 0;">Scheduled</td>
          <td style="padding:3px 0; color:#0F172A;">${scheduledFor}</td>
          <td style="color:#6B7280; padding:3px 0;">Completed</td>
          <td style="padding:3px 0; color:#0F172A;">${completedAt}</td>
        </tr>
        ${arrivedAt ? `<tr><td style="color:#6B7280; padding:3px 0;">Arrived on site</td><td style="padding:3px 0; color:#0F172A;" colspan="3">${arrivedAt}</td></tr>` : ""}
      </table>
      ${workDone ? `<div style="margin-top:6px; padding:8px 10px; background:#F9FAFB; border-radius:6px;"><div style="font-size:11pt; font-weight:800; color:#111827; margin-bottom:2px;">Work Summary</div><div style="font-size:10pt; color:#0F172A; line-height:1.5;">${workDone.replace(/\n/g, "<br/>")}</div></div>` : ""}
      ${items.length > 0 ? `<div style="margin-top:6px;"><div style="font-size:11pt; font-weight:800; color:#111827; margin-bottom:4px;">Required Repairs</div><ul style="margin:0 0 0 18px; padding:0; font-size:10pt; color:#0F172A;">${items.map((it) => `<li style="margin-bottom:3px;"><span style="font-weight:600;">${esc(it.component)}</span>${it.description ? ` — ${esc(stripCurrency(it.description))}` : ""}</li>`).join("")}</ul></div>` : ""}
      ${itemCompletions.length > 0 ? `<div style="margin-top:10px;"><div style="font-size:11pt; font-weight:800; color:#111827; margin-bottom:4px;">Work Carried Out</div>${itemCompletions.map((ic) => `<div style="border-left:4px solid ${ic.fixed ? "#10B981" : "#F59E0B"}; padding:8px 10px; margin-bottom:6px; background:#F9FAFB; border-radius:0 6px 6px 0;"><div style="font-size:10pt; font-weight:700; color:${ic.fixed ? "#065F46" : "#78350F"};">${ic.fixed ? "✓" : "⚠"} ${esc(ic.component)}</div>${ic.notes ? `<div style="font-size:9.5pt; color:#374151; margin-top:2px;">${esc(ic.notes)}</div>` : ""}${photoGrid(ic.photos, "Evidence")}</div>`).join("")}</div>` : ""}
      ${materials.length > 0 ? `<div style="margin-top:10px;"><div style="font-size:11pt; font-weight:800; color:#111827; margin-bottom:4px;">Materials Used</div>${materials.map((m) => `<div style="border:1px solid #E5E7EB; padding:8px 10px; margin-bottom:6px; border-radius:6px; background:#fff;"><div style="font-size:10pt; font-weight:700; color:#0F172A;">${esc(m.name)}</div>${m.notes ? `<div style="font-size:9.5pt; color:#374151; margin-top:2px;">${esc(m.notes)}</div>` : ""}${photoGrid(m.photos, "Material")}</div>`).join("")}</div>` : ""}
      ${parts.length > 0 ? `<div style="margin-top:10px;"><div style="font-size:11pt; font-weight:800; color:#111827; margin-bottom:4px;">Parts Replaced</div><ul style="margin:0 0 0 18px; padding:0; font-size:10pt; color:#0F172A;">${parts.map((p) => `<li>${esc(p)}</li>`).join("")}</ul></div>` : ""}
      ${generalPhotos.length > 0 ? `<div style="margin-top:10px;"><div style="font-size:11pt; font-weight:800; color:#111827; margin-bottom:4px;">Photographs of Completed Works</div>${photoGrid(generalPhotos, "Completed")}</div>` : ""}
      ${pw ? `<div style="margin-top:10px; padding:10px 12px; background:#ECFDF5; border-left:4px solid #059669; border-radius:0 6px 6px 0;"><div style="font-size:10pt; font-weight:800; color:#065F46;">Verified by post-work inspection</div><div style="font-size:9.5pt; color:#064E3B; margin-top:2px;">Re-inspected ${fmtDate(pw.inspection_date)}${pw.inspector_name ? ` by ${esc(pw.inspector_name)}` : ""} — outcome <strong>${esc(String(pw.status || "").replace("_", " ").toUpperCase())}</strong>. Added to the building&rsquo;s Golden Thread.</div></div>` : (job?.requires_reinspection ? `<div style="margin-top:10px; padding:10px 12px; background:#FFFBEB; border-left:4px solid #D97706; border-radius:0 6px 6px 0;"><div style="font-size:10pt; font-weight:800; color:#92400E;">Post-work inspection pending</div><div style="font-size:9.5pt; color:#78350F; margin-top:2px;">This door is flagged for an independent re-inspection to confirm the repair.</div></div>` : "")}
      ${notes ? `<div style="margin-top:10px; padding:10px 12px; background:#EFF6FF; border-left:4px solid #2563EB; border-radius:0 6px 6px 0;"><div style="font-size:10pt; font-weight:800; color:#1E3A8A;">Engineer notes</div><div style="font-size:10pt; color:#1E3A8A; margin-top:3px; line-height:1.5;">${notes}</div></div>` : ""}
      ${signature ? `<div style="margin-top:12px; padding-top:8px; border-top:1px dashed #E5E7EB; font-size:10pt;"><span style="color:#6B7280;">Signed by engineer:</span> <span style="font-weight:800; color:#0F172A; font-style:italic;">${signature}</span>${completedAt !== "—" ? ` <span style="color:#6B7280;"> · ${completedAt}</span>` : ""}</div>` : ""}
    </div>`;
}

function triggerDownload(url, filename) {
  if (!url) return;
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "completion-report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) { console.warn("[pdf] download trigger failed (preview tab still works)", e); }
}

export async function generateMaintenanceReportForJob(job) {
  const brand = await resolveBrand(job);
  const doorRef = job?.door_reference || job?.door?.door_reference || "Door";
  const filename = `Completion-Report-${String(doorRef).replace(/[^A-Za-z0-9_-]+/g, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`;
  const site = [job?.client_name, job?.building_name || job?.building?.name].map((v) => String(v || "").trim()).filter(Boolean).join(" · ");
  const html = `
    <!DOCTYPE html><html><head><meta charset="utf-8" /><title>${esc(filename)}</title>
    <style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#111827;padding:20px;background:#fff;}@page{size:A4 portrait;margin:12mm;}</style>
    </head><body>
      ${headerHtml(brand, "JOB COMPLETION REPORT", site || "Fire door remedial works")}
      <div style="font-size:10.5pt; color:#374151; margin-bottom:10px;">
        Remedial works carried out by <span style="font-weight:800; color:#0F172A;">${esc(brand.name)}</span>${job?.building_address || job?.building?.address ? ` at ${esc(job?.building_address || job?.building?.address)}` : ""}. This report records the work completed, the materials used and the photographic evidence captured on site.
      </div>
      ${jobSectionHtml(job)}
      ${footerHtml(brand)}
    </body></html>`;
  const url = await htmlToPdf(html, filename.replace(/\.pdf$/, ""));
  triggerDownload(url, filename);
  return url;
}
